import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import Header from '../components/Header';
import NavTabs from '../components/NavTabs';

const MEDICATION_OPTIONS = [
  { value: '',              label: 'All medications' },
  { value: 'buprenorphine', label: 'Buprenorphine' },
  { value: 'methadone',     label: 'Methadone' },
  { value: 'naltrexone',    label: 'Naltrexone' },
];

const MED_BADGE = {
  offers_buprenorphine: { label: 'Buprenorphine', cls: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800' },
  offers_methadone:     { label: 'Methadone',     cls: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800' },
  offers_naltrexone:    { label: 'Naltrexone',    cls: 'bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-900/30 dark:text-teal-300 dark:border-teal-800' },
};

function formatDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
}

function Spinner() {
  return (
    <div className="flex justify-center py-16">
      <svg className="animate-spin w-8 h-8 text-blue-500" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
      </svg>
    </div>
  );
}

function ProgramCard({ program }) {
  const [showDirections, setShowDirections] = useState(false);
  const location = [
    program.physical_street,
    program.physical_city,
    program.state && program.state !== 'WA' ? program.state : null,
    program.physical_zip,
  ].filter(Boolean).join(', ');

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-base font-bold text-gray-900 dark:text-gray-100 leading-snug">{program.program_name}</h3>
        <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end">
          {Object.entries(MED_BADGE).map(([key, badge]) => program[key] ? (
            <span key={key} className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap border ${badge.cls}`}>
              {badge.label}
            </span>
          ) : null)}
        </div>
      </div>

      <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        {location && <p>{location}</p>}
        {program.physical_county && <p>{program.physical_county} County</p>}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
        {program.phone && (
          <a href={`tel:${program.phone}`} className="text-blue-600 hover:underline text-sm font-medium">{program.phone}</a>
        )}
        {program.website_url && (
          <a href={program.website_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">
            Visit website →
          </a>
        )}
      </div>

      {program.hours && (
        <p className="mt-2 text-xs text-gray-600 dark:text-gray-300">
          <span className="font-medium text-gray-700 dark:text-gray-200">Hours: </span>{program.hours}
        </p>
      )}
      {program.languages && (
        <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">
          <span className="font-medium text-gray-700 dark:text-gray-200">Languages: </span>{program.languages}
        </p>
      )}

      {program.description && (
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{program.description}</p>
      )}

      {program.transportation && (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => setShowDirections((v) => !v)}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
          >
            {showDirections ? 'Hide directions' : 'Directions & transit'}
          </button>
          {showDirections && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 italic leading-relaxed">{program.transportation}</p>
          )}
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center py-20 text-center">
      <svg className="w-14 h-14 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
      </svg>
      <p className="text-gray-500 font-medium">No programs match your filters</p>
      <p className="text-gray-400 text-sm mt-1">Try a different county, medication, or search term</p>
    </div>
  );
}

export default function Moud() {
  const [programs, setPrograms]   = useState([]);
  const [counties, setCounties]   = useState([]);
  const [meta, setMeta]           = useState({});
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [county, setCounty]       = useState('');
  const [medication, setMedication] = useState('');
  const [search, setSearch]       = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    axios.get('/api/moud/counties').then((r) => setCounties(r.data || [])).catch(() => {});
    axios.get('/api/moud/meta').then((r) => setMeta(r.data || {})).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback((c, m, s) => {
    setLoading(true);
    setError('');
    const params = {};
    if (c) params.county = c;
    if (m) params.medication = m;
    if (s) params.search = s;
    axios.get('/api/moud', { params })
      .then((r) => setPrograms(r.data || []))
      .catch(() => setError('Could not load MOUD programs.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(county, medication, debounced); }, [county, medication, debounced, load]);

  const lastUpdated = ['buprenorphine', 'methadone', 'naltrexone']
    .map((m) => ({ m, d: formatDate(meta[m]) }))
    .filter((x) => x.d);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Helmet>
        <title>MOUD Programs | WA BHA Treatment Finder</title>
        <meta name="description" content="Find buprenorphine, methadone, and naltrexone providers in Washington State. Medications for opioid use disorder (MOUD) directory with hours, phone, and languages." />
      </Helmet>
      <Header right={null} />
      <NavTabs />

      <div className="max-w-7xl mx-auto px-4 pt-5 pb-12">
        <div className="mb-5">
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">MOUD Programs</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Where to start or continue medications for opioid use disorder — buprenorphine, methadone, and naltrexone — across Washington State.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-5">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, city, or services…"
            className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400 w-64 max-w-full"
          />
          <select
            value={county}
            onChange={(e) => setCounty(e.target.value)}
            className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            <option value="">All Counties</option>
            {counties.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <div className="flex items-center gap-1 flex-wrap">
            {MEDICATION_OPTIONS.map((o) => (
              <button
                key={o.value}
                onClick={() => setMedication(o.value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                  medication === o.value
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
          {(county || medication || search) && (
            <button onClick={() => { setCounty(''); setMedication(''); setSearch(''); }} className="text-xs text-blue-600 hover:underline">
              Clear filters
            </button>
          )}
        </div>

        {!loading && !error && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Showing {programs.length} {programs.length === 1 ? 'program' : 'programs'}
          </p>
        )}

        {loading ? <Spinner /> : error ? (
          <p className="text-sm text-red-600">{error}</p>
        ) : programs.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {programs.map((p) => <ProgramCard key={p.id} program={p} />)}
          </div>
        )}

        <div className="mt-10 border-t border-gray-200 dark:border-gray-700 pt-4 text-xs text-gray-400 dark:text-gray-500 space-y-1">
          <p>
            Source: Washington Recovery Help Line (1-866-789-1511, search.warecoveryhelpline.org).
            {lastUpdated.length > 0 && (
              <> Lists updated: {lastUpdated.map((x, i) => `${x.m} ${x.d}`).join(' · ')}.</>
            )}
          </p>
          <p>Confirm hours and admission details with the program before referring. Listings are provided as a public service without warranty.</p>
        </div>
      </div>
    </div>
  );
}
